import * as React from "react";
import { api, postJson } from "./api";
import { AppLink, useNavigation } from "./router";
import type { AppUser } from "./room-session";

interface ImportCandidate {
  trackId: string;
  title: string;
  artist: string;
  album: string | null;
  artworkUrl: string | null;
  spotifyUrl: string;
  likedAt: string | null;
  liked: boolean;
  playlistCount: number;
  playlistNames: string[];
  rankBucket: "recent_like" | "older_like" | "playlist_staple" | "backfill" | "alternate";
  proposedRank: number;
  rank: number | null;
  included: boolean;
  diff: "added" | "unchanged";
}

interface RemovedTrack {
  trackId: string;
  title: string;
  artist: string;
  album: string | null;
  artworkUrl: string | null;
  spotifyUrl: string;
  rank: number;
  diff: "removed";
}

interface SpotifyImportPayload {
  scopes: { connected: boolean; ready: boolean; missingScopes: string[] };
  run: null | {
    id: string;
    status: "collecting" | "ready" | "published" | "failed" | "cancelled";
    phase: "liked" | "playlists" | "playlist_items" | "ranking" | "complete";
    progress: {
      liked: number;
      likedTotal: number | null;
      playlists: number;
      playlistsTotal: number | null;
      playlistItems: number;
    };
    retryAfterAt: string | null;
    error: null | { code: string; message: string };
    bio: string;
    libraryTitle: string;
    selectedCount: number;
    candidateCount: number;
    createdAt: string;
    readyAt: string | null;
    publishedAt: string | null;
  };
  candidates: ImportCandidate[];
  removed: RemovedTrack[];
  featured: { count: number; publishedAt: string | null };
}

function phaseCopy(payload: SpotifyImportPayload) {
  const run = payload.run;
  if (!run) return { title: "Preparing your importer", detail: "Getting your private workspace ready." };
  if (run.retryAfterAt && Date.parse(run.retryAfterAt) > Date.now()) {
    return { title: "Spotify asked us to slow down", detail: "Your progress is saved. Jamband will resume automatically." };
  }
  if (run.phase === "liked") {
    return {
      title: "Reading your Liked Songs",
      detail: run.progress.likedTotal == null
        ? `${run.progress.liked} songs collected`
        : `${run.progress.liked} of ${run.progress.likedTotal} songs collected`,
    };
  }
  if (run.phase === "playlists") {
    return { title: "Finding your playlists", detail: `${run.progress.playlists} owned or collaborative playlists found` };
  }
  if (run.phase === "playlist_items") {
    return { title: "Finding playlist staples", detail: `${run.progress.playlistItems} distinct playlist appearances checked` };
  }
  return { title: "Building your Featured 60", detail: "Applying the artist, album, history, and source mix." };
}

function bucketCopy(bucket: ImportCandidate["rankBucket"]) {
  if (bucket === "recent_like") return "Recent like";
  if (bucket === "older_like") return "Across your history";
  if (bucket === "playlist_staple") return "Playlist staple";
  if (bucket === "backfill") return "Like backfill";
  return "Alternate";
}

export function SpotifyImportPage({ handle, user, onReconnect }: {
  handle: string;
  user: AppUser | null;
  onReconnect: () => void;
}) {
  const { navigate } = useNavigation();
  const [payload, setPayload] = React.useState<SpotifyImportPayload | null>(null);
  const [candidates, setCandidates] = React.useState<ImportCandidate[]>([]);
  const [bio, setBio] = React.useState("Songs I liked enough to keep.");
  const [libraryTitle, setLibraryTitle] = React.useState("Sean’s rotation");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const mountedRef = React.useRef(true);
  const collectingRef = React.useRef(false);

  React.useEffect(() => () => { mountedRef.current = false; }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPayload(await api<SpotifyImportPayload>("/api/me/spotify-import"));
    } catch (cause: any) {
      setError(cause?.message || "Could not open the Spotify importer");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);
  React.useEffect(() => {
    if (payload?.run?.status !== "ready") return;
    setCandidates(payload.candidates);
    setBio(payload.run.bio);
    setLibraryTitle(payload.run.libraryTitle);
  }, [payload]);

  const continueUntilReady = React.useCallback(async (runId: string) => {
    if (collectingRef.current) return;
    collectingRef.current = true;
    setBusy("collecting");
    setError("");
    try {
      while (mountedRef.current) {
        const next = await postJson<SpotifyImportPayload>(`/api/me/spotify-import/${encodeURIComponent(runId)}/continue`, {});
        if (!mountedRef.current) return;
        setPayload(next);
        if (next.run?.status !== "collecting") break;
        if (next.run.retryAfterAt && Date.parse(next.run.retryAfterAt) > Date.now()) {
          const delay = Math.max(1_000, Date.parse(next.run.retryAfterAt) - Date.now() + 250);
          window.setTimeout(() => {
            collectingRef.current = false;
            if (mountedRef.current) void continueUntilReady(runId);
          }, delay);
          return;
        }
      }
    } catch (cause: any) {
      setError(cause?.message || "The import paused. You can safely resume it.");
    } finally {
      if (mountedRef.current) setBusy("");
      collectingRef.current = false;
    }
  }, []);

  React.useEffect(() => {
    if (payload?.run?.status === "collecting") void continueUntilReady(payload.run.id);
  }, [continueUntilReady, payload?.run?.id, payload?.run?.status]);

  const start = async () => {
    setBusy("starting");
    setError("");
    try {
      const next = await postJson<SpotifyImportPayload>("/api/me/spotify-import", {});
      setPayload(next);
      if (next.run) void continueUntilReady(next.run.id);
    } catch (cause: any) {
      setError(cause?.message || "Could not start the import");
    } finally { setBusy(""); }
  };

  const selected = candidates.filter((candidate) => candidate.included);
  const alternates = candidates.filter((candidate) => !candidate.included);

  const toggle = (trackId: string) => {
    setError("");
    setCandidates((current) => {
      const target = current.find((candidate) => candidate.trackId === trackId);
      if (!target) return current;
      const currentSelected = current.filter((candidate) => candidate.included);
      if (!target.included && currentSelected.length >= 60) {
        setError("Remove one Featured song before adding an alternate.");
        return current;
      }
      const updated = current.map((candidate) => candidate.trackId === trackId
        ? { ...candidate, included: !candidate.included }
        : candidate);
      return [...updated.filter((candidate) => candidate.included), ...updated.filter((candidate) => !candidate.included)];
    });
  };

  const move = (trackId: string, direction: -1 | 1) => {
    setCandidates((current) => {
      const included = current.filter((candidate) => candidate.included);
      const rest = current.filter((candidate) => !candidate.included);
      const index = included.findIndex((candidate) => candidate.trackId === trackId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= included.length) return current;
      [included[index], included[nextIndex]] = [included[nextIndex]!, included[index]!];
      return [...included, ...rest];
    });
  };

  const saveDraft = async () => {
    if (!payload?.run) return null;
    const next = await api<SpotifyImportPayload>(`/api/me/spotify-import/${encodeURIComponent(payload.run.id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ orderedTrackIds: selected.map((candidate) => candidate.trackId), bio, libraryTitle }),
    });
    setPayload(next);
    return next;
  };

  const publish = async () => {
    if (!payload?.run || selected.length < 1) return;
    setBusy("publishing");
    setError("");
    try {
      await saveDraft();
      await postJson(`/api/me/spotify-import/${encodeURIComponent(payload.run.id)}/publish`, {});
      navigate(`/u/${encodeURIComponent(handle)}`);
    } catch (cause: any) {
      setError(cause?.message || "Could not publish Featured");
    } finally { setBusy(""); }
  };

  const cancel = async () => {
    if (!payload?.run) return;
    setBusy("cancelling");
    try {
      setPayload(await api<SpotifyImportPayload>(`/api/me/spotify-import/${encodeURIComponent(payload.run.id)}`, { method: "DELETE" }));
    } catch (cause: any) {
      setError(cause?.message || "Could not cancel the draft");
    } finally { setBusy(""); }
  };

  if (loading) return <main className="profile-loading page-shell"><div className="profile-skeleton"><div /><div /><div /></div></main>;
  if (!user || user.handle.toLowerCase() !== handle.toLowerCase()) {
    return <main className="not-found page-shell"><span>403</span><h1>This import studio belongs to the profile owner.</h1><AppLink href={`/u/${encodeURIComponent(handle)}`}>Back to profile</AppLink></main>;
  }
  if (!payload) return <main className="not-found page-shell"><span>!</span><h1>{error || "Importer unavailable"}</h1><button className="button primary" onClick={() => void load()}>Try again</button></main>;

  const phase = phaseCopy(payload);
  const addedCount = selected.filter((candidate) => candidate.diff === "added").length;
  const unchangedCount = selected.filter((candidate) => candidate.diff === "unchanged").length;

  return (
    <main className="spotify-import-page">
      <div className="page-shell import-shell">
        <header className="import-heading">
          <div><span className="eyebrow">PRIVATE PROFILE STUDIO</span><h1>Build your Featured 60</h1><p>Jamband reads your Spotify collection, proposes a balanced public rotation, and lets you approve every song.</p></div>
          <AppLink className="button ghost" href={`/u/${encodeURIComponent(handle)}`}>← Back to profile</AppLink>
        </header>

        {!payload.scopes.ready ? (
          <section className="import-intro profile-card">
            <div className="import-record" aria-hidden="true"><span>60</span><i /></div>
            <div><span className="eyebrow">ONE READ-ONLY RECONNECT</span><h2>Let Jamband see your Liked Songs and playlists</h2><p>Spotify requires fresh permission before Jamband can read this part of your account. Jamband will never change your likes or playlists.</p><ul><li>Your raw collection and playlist names stay private.</li><li>Only songs you publish to Featured become public.</li><li>The ranking is fixed rules, not AI taste inference.</li></ul><button className="button spotify-button" onClick={onReconnect}><i>●</i> Reconnect Spotify</button></div>
          </section>
        ) : payload.run?.status === "collecting" ? (
          <section className="import-progress profile-card" aria-live="polite">
            <div className="signal-loader" aria-hidden="true"><i /><i /><i /></div>
            <span className="eyebrow">IMPORT IN PROGRESS · SAFE TO LEAVE</span><h2>{phase.title}</h2><p>{phase.detail}</p>
            <div className="import-progress-grid"><span><b>{payload.run.progress.liked}</b> Liked Songs</span><span><b>{payload.run.progress.playlists}</b> playlists</span><span><b>{payload.run.progress.playlistItems}</b> playlist signals</span></div>
            <p className="privacy-note">Progress is saved after every page. Private Spotify details never appear on your public profile.</p>
            {error && <p className="field-error" role="status">{error}</p>}
            <div className="import-actions"><button className="button ghost" onClick={() => void cancel()} disabled={busy === "cancelling"}>Cancel draft</button>{error && <button className="button primary" onClick={() => void continueUntilReady(payload.run!.id)}>Resume</button>}</div>
          </section>
        ) : payload.run?.status === "ready" ? (
          <>
            <section className="import-copy profile-card">
              <header><div><span className="eyebrow">PROFILE COPY</span><h2>Set the shelf label</h2></div><span className="spotify-attribution">● Spotify-powered</span></header>
              <div className="import-copy-fields"><label><span>Bio</span><textarea maxLength={280} value={bio} onChange={(event) => setBio(event.target.value)} /></label><label><span>Library title</span><input maxLength={80} value={libraryTitle} onChange={(event) => setLibraryTitle(event.target.value)} /></label></div>
            </section>

            <section className="import-diff-strip" aria-label="Changes from current Featured"><span className="added"><b>{addedCount}</b> Added</span><span className="unchanged"><b>{unchangedCount}</b> Unchanged</span><span className="removed"><b>{payload.removed.length}</b> Removed</span><span className="selected-count"><b>{selected.length}</b> / 60 selected</span></section>

            <section className="import-candidates profile-card">
              <header><div><span className="eyebrow">PUBLIC PREVIEW</span><h2>Your proposed rotation</h2><p>Up and down controls set public order. Remove a pick before adding an alternate when you have 60.</p></div><button className="button ghost" disabled={Boolean(busy)} onClick={() => void start()}>Start fresh</button></header>
              {error && <p className="field-error" role="status">{error}</p>}
              <ol className="import-track-list">
                {selected.map((candidate, index) => <li className="import-track selected" key={candidate.trackId}>
                  <span className="import-rank">{index + 1}</span><div className="track-art">{candidate.artworkUrl ? <img src={candidate.artworkUrl} width="64" height="64" loading="lazy" alt="" /> : "♪"}</div>
                  <div className="import-track-copy"><strong>{candidate.title}</strong><span>{candidate.artist}{candidate.album ? ` · ${candidate.album}` : ""}</span><small><b>{bucketCopy(candidate.rankBucket)}</b>{candidate.liked ? " · Liked Song" : ""}{candidate.playlistCount ? ` · ${candidate.playlistCount} playlist${candidate.playlistCount === 1 ? "" : "s"}` : ""}</small>{candidate.playlistNames.length > 0 && <em title="Visible only to you">Private sources: {candidate.playlistNames.join(", ")}</em>}</div>
                  <span className={`diff-chip ${candidate.diff}`}>{candidate.diff}</span>
                  <div className="import-order-controls"><button onClick={() => move(candidate.trackId, -1)} disabled={index === 0} aria-label={`Move ${candidate.title} up`}>↑</button><button onClick={() => move(candidate.trackId, 1)} disabled={index === selected.length - 1} aria-label={`Move ${candidate.title} down`}>↓</button><button className="remove" onClick={() => toggle(candidate.trackId)} aria-label={`Remove ${candidate.title} from Featured`}>×</button></div>
                </li>)}
              </ol>
            </section>

            {alternates.length > 0 && <section className="import-candidates alternates profile-card"><header><div><span className="eyebrow">PRIVATE ALTERNATES</span><h2>Other strong candidates</h2></div></header><div className="alternate-grid">{alternates.map((candidate) => <article className="alternate-track" key={candidate.trackId}><div className="track-art">{candidate.artworkUrl ? <img src={candidate.artworkUrl} width="54" height="54" loading="lazy" alt="" /> : "♪"}</div><div><strong>{candidate.title}</strong><span>{candidate.artist}</span><small>{candidate.liked ? "Liked Song" : `${candidate.playlistCount} playlists`}</small></div><button onClick={() => toggle(candidate.trackId)} disabled={selected.length >= 60}>＋ Add</button></article>)}</div></section>}

            {payload.removed.length > 0 && <details className="removed-preview profile-card"><summary>{payload.removed.length} song{payload.removed.length === 1 ? "" : "s"} would leave Featured</summary><div>{payload.removed.map((track) => <span key={track.trackId}><b>{track.title}</b> · {track.artist}</span>)}</div></details>}

            <footer className="import-publish-bar"><div><span className="eyebrow">READY WHEN YOU ARE</span><strong>{selected.length} public Featured song{selected.length === 1 ? "" : "s"}</strong><small>Saved in Jamband stays untouched.</small></div><button className="button primary" disabled={Boolean(busy) || selected.length < 1} onClick={() => void publish()}>{busy === "publishing" ? "Publishing…" : "Publish Featured"}</button></footer>
          </>
        ) : (
          <section className="import-intro profile-card">
            <div className="import-record" aria-hidden="true"><span>{payload.featured.count || 60}</span><i /></div>
            <div><span className="eyebrow">SPOTIFY-POWERED PROFILE</span><h2>{payload.featured.count ? "Refresh your public rotation" : "Turn your collection into a profile"}</h2><p>{payload.featured.count ? `Your current Featured has ${payload.featured.count} songs. A refresh creates a private draft and shows every change before publishing.` : "Jamband will collect your accessible Spotify music, prepare 120 private candidates, and propose up to 60 for you to approve."}</p><button className="button primary" disabled={Boolean(busy)} onClick={() => void start()}>{busy === "starting" ? "Starting…" : payload.featured.count ? "Start a refresh" : "Build my Featured 60"}</button>{payload.run?.status === "failed" && <p className="field-error">{payload.run.error?.message || "The last import stopped before it was ready."}</p>}</div>
          </section>
        )}
        <p className="import-spotify-footer">Music metadata provided by <a href="https://open.spotify.com" target="_blank" rel="noreferrer">Spotify ↗</a>. Jamband never writes to your Spotify account.</p>
      </div>
    </main>
  );
}
